/**
 * ViewportWorkspaceContext — one ViewportWorkspace per LAYOUT (A4 consolidation C1).
 *
 * The unified path (default render) mounts a `SessionVolumeMount` per pane
 * (3 panes in a labeling layout). Each mount previously created its OWN `ViewportWorkspace`
 * (`new ViewportWorkspace(...); mount(); … dispose()`), so 3 panes produced 3
 * Workspaces sharing the singleton rendering engine AND 3 sets of
 * webglcontextlost listeners — a dual-/triple-context-loss-listener bug.
 *
 * To consolidate, `HangingProtocolGrid` (the layout owner) creates ONE
 * `ViewportWorkspace` for the whole grid and publishes it through this context.
 * Each `SessionVolumeMount` reads it via `useSharedWorkspace()` and calls
 * `addSession` / `removeSession` against the shared instance instead of building
 * its own. The grid's `dispose()` is the ONLY place the engine is destroyed.
 *
 * Default value is `null`: when the flag is OFF (and on any tree that doesn't
 * mount the provider) the context yields `null`, and consumers fall back to
 * their previous per-pane behavior so nothing regresses.
 */
import { createContext, useContext } from "react";
import type { ViewportWorkspace } from "../../viewport/ViewportWorkspace";

/** The shared, layout-scoped workspace, or null when none is provided. */
export const ViewportWorkspaceContext = createContext<ViewportWorkspace | null>(null);

/** Returns the layout-shared ViewportWorkspace, or null when unprovided. */
export function useSharedWorkspace(): ViewportWorkspace | null {
  return useContext(ViewportWorkspaceContext);
}
